/**
 * Reads the downloads warehouse that `storeReports.ts` fills.
 *
 * One document per day, id `YYYY-MM-DD`, written only by the collector. The
 * client never writes here and the rules forbid it: Apple deletes a daily
 * report after a year, so a row overwritten by mistake could not be fetched
 * again.
 *
 * ## Coverage is part of the answer
 *
 * Neither store has always been readable, and the two ran out at different
 * points, so a bare total would be a number with a hole in it:
 *
 * - **Google Play** publishes monthly files that stay put. Ours reach back to
 *   March 2024, though Android in earnest starts October 2024.
 * - **Apple** serves a daily report for 365 days and then deletes it. Whatever
 *   the collector has stored is all there will ever be for older days, and for
 *   anything before the collector first ran there is nothing.
 *
 * So every read reports which days actually carry an Apple figure alongside the
 * total, and the screen says so rather than presenting a short number as
 * complete.
 */
import { collection, getDocs, query, where, documentId } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'

export interface StoreMonth {
  /** `YYYY-MM`. */
  month: string
  apple: number
  play: number
  downloads: number
  /** Days in the month with an Apple figure, and days in the month elapsed. */
  appleDays: number
  days: number
}

export interface StoreDownloads {
  months: StoreMonth[]
  apple: number
  play: number
  total: number
  /** Days across the range with neither a store figure nor a CSV one. */
  missingDays: number
  /**
   * Days whose figure comes from the hand-exported CSV rather than a live
   * report. Not a defect — for anything older than Apple's one-year window it
   * is the only surviving record — but the screens say so rather than implying
   * every figure came from an API.
   */
  csvDays: number
  /** Earliest day in the range with any figure at all. `null` when empty. */
  firstDay: string | null
}

interface DayDoc {
  appleFirstTime?: number
  playFirstTime?: number
  /** Backfilled from the downloads export — see `importDownloadsCsv.ts`. */
  appleCsv?: number
  playCsv?: number
}

/**
 * Totals for a month range, inclusive, both `YYYY-MM`.
 *
 * The document ids are dates, so the range is a document-id query and needs no
 * index — `-01` opens the first month and `-32` closes the last one above every
 * real day in it.
 */
export async function loadDownloads(
  fromMonth: string,
  toMonth: string,
): Promise<StoreDownloads> {
  const snap = await getDocs(
    query(
      collection(db, 'skidaCrmStoreDays'),
      where(documentId(), '>=', `${fromMonth}-01`),
      where(documentId(), '<=', `${toMonth}-32`),
    ),
  )

  const byMonth = new Map<string, StoreMonth>()
  let apple = 0
  let play = 0
  let missingDays = 0
  let csvDays = 0
  let firstDay: string | null = null

  const today = new Date().toISOString().slice(0, 10)

  for (const d of snap.docs) {
    const day = d.id
    // A day the season has not reached yet is not a gap in the data.
    if (day > today) continue
    const v = d.data() as DayDoc
    const month = day.slice(0, 7)
    const row =
      byMonth.get(month) ??
      { month, apple: 0, play: 0, downloads: 0, appleDays: 0, days: 0 }

    // The store first, the export second. Where a live report exists it is what
    // the store itself says today; the CSV is a copy taken by hand on some
    // earlier day, and only it can speak for the days Apple has since deleted.
    const appleDay = v.appleFirstTime ?? v.appleCsv
    const playDay = v.playFirstTime ?? v.playCsv

    if (appleDay !== undefined) {
      row.apple += appleDay
      row.appleDays++
      apple += appleDay
    }
    if (playDay !== undefined) {
      row.play += playDay
      play += playDay
    }
    if (appleDay === undefined && playDay === undefined) missingDays++
    if (v.appleFirstTime === undefined && v.appleCsv !== undefined) csvDays++

    row.days++
    row.downloads = row.apple + row.play
    byMonth.set(month, row)

    if (!firstDay || day < firstDay) firstDay = day
  }

  return {
    months: [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month)),
    apple,
    play,
    total: apple + play,
    missingDays,
    csvDays,
    firstDay,
  }
}
